import {
    HStack,
    VStack,
    Button,
    IconButton,
    Text,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import {
    EventSearchRow,
    FieldNameEnum,
    GenerateReportingEventNumberResponse,
    ReportingEventNumber,
    ReportingEventNumberDto,
} from '@mark43/cad-api';
import { useState } from 'react';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import {
    DisplayReportingEventNumber,
    useDisplayReportingEventNumbers,
} from '../common/hooks/use-display-reporting-event-numbers';
import { useGenerateReportingEventNumber } from '../common/hooks/use-generate-reporting-event-number';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useOpenDepartmentLink } from '../layout/use-open-department-link';

export function EventSearchResultsReportingEventNumbersCell({
    eventSearchRow,
}: {
    eventSearchRow: EventSearchRow;
}) {
    const enabledFeatures = useEnabledFeatures();
    const [row, setRow] = useState<EventSearchRow>(eventSearchRow);
    const [
        generateReportingEventNumber,
        { isLoading: isGenerateRenLoading },
    ] = useGenerateReportingEventNumber();
    const displayRens = useDisplayReportingEventNumbers(eventSearchRow.reportingEventNumbers, {
        omitAgencyAbbreviation: true,
    });
    const truncatedRens = displayRens.slice(0, 3);
    const overflowRens = displayRens.slice(3);
    const primaryAgencyRens = row.reportingEventNumbers.filter(
        (ren) => ren.agencyId === row.agencyId
    );
    return (
        <HStack justifyContent="space-between" alignItems="center">
            <VStack align="left">
                {truncatedRens.map((ren, index) => {
                    return (
                        <HStack key={ren.id}>
                            <Text>{ren.display}</Text>
                            {ren.searchRmsLink && <RenLink searchRmsLink={ren.searchRmsLink} />}
                            {index === truncatedRens.length - 1 && overflowRens.length > 0 && (
                                <OverflowRensPopover rens={overflowRens} />
                            )}
                        </HStack>
                    );
                })}
            </VStack>
            {row.canGenerateRen && (
                <Button
                    leadingVisual="Add"
                    variant="outline"
                    isLoading={isGenerateRenLoading}
                    onClick={() =>
                        generateReportingEventNumber({
                            agencyEventId: row.eventId,
                            agencyEventNumber: row.eventNumber,
                            numberOfRensForPrimaryAgency: primaryAgencyRens.length,
                        }).then((response?: GenerateReportingEventNumberResponse) => {
                            const newReportingEventNumber = response?.reportingEventNumbers?.[0];

                            if (newReportingEventNumber) {
                                setRow({
                                    ...row,
                                    reportingEventNumbers: [
                                        ...row.reportingEventNumbers,
                                        toReportingEventNumberDto(
                                            newReportingEventNumber,
                                            row.eventNumber
                                        ),
                                    ],
                                    canGenerateRen:
                                        row.canGenerateRen && enabledFeatures.multiRenGeneration,
                                });
                            }
                        })
                    }
                >
                    Generate
                </Button>
            )}
        </HStack>
    );
}

function RenLink({ searchRmsLink }: { searchRmsLink: string }) {
    const openDepartmentLink = useOpenDepartmentLink();
    const { fieldDisplayNameByFieldName } = useBootstrapState();
    const renDisplayName =
        fieldDisplayNameByFieldName[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];
    return (
        <IconButton
            variant="ghost"
            icon="OpenNewWindow"
            aria-label={`search ${renDisplayName} in RMS`}
            onClick={() =>
                openDepartmentLink({
                    tenantStrategy: 'PREFER_RMS_TENANT',
                    path: searchRmsLink,
                    app: 'RMS',
                })
            }
        />
    );
}

function OverflowRensPopover({ rens }: { rens: DisplayReportingEventNumber[] }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="ghost">{` +${rens.length}`}</Button>
            </PopoverTrigger>
            <PopoverContent>
                <VStack align="left">
                    {rens.map((ren) => {
                        return (
                            <HStack key={ren.id}>
                                <Text>{ren.display}</Text>
                                {ren.searchRmsLink && <RenLink searchRmsLink={ren.searchRmsLink} />}
                            </HStack>
                        );
                    })}
                </VStack>
            </PopoverContent>
        </Popover>
    );
}

function toReportingEventNumberDto(
    reportingEventNumber: ReportingEventNumber,
    agencyEventNumber: string
): ReportingEventNumberDto {
    return {
        id: reportingEventNumber.id,
        agencyId: reportingEventNumber.agencyId,
        reportingEventNumber: reportingEventNumber.reportingEventNumber,
        agencyEventId: reportingEventNumber.agencyEventId,
        agencyEventNumber,
    };
}
